import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './Meghalaya.css';

const Meghalaya = () => {
  return (
    <>
      <Helmet>
        <title>Meghalaya Tour - NomadTrailz</title>
        <meta name="description" content="Explore the abode of clouds - Meghalaya with NomadTrailz. Living root bridges, pristine waterfalls, and rich tribal culture." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755000311/qmgldyf6dkmqdkaqtd7s.jpg?v=2" 
              alt="Meghalaya" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Meghalaya
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              The abode of clouds with living root bridges and pristine waterfalls
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Meghalaya, meaning "abode of clouds," is a northeastern state of India known 
                    for its stunning landscapes, living root bridges, pristine waterfalls, and 
                    rich tribal culture. This mystical land offers a perfect blend of adventure, 
                    culture, and natural beauty.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Living root bridges of Cherrapunji</li>
                    <li>Majestic waterfalls and caves</li>
                    <li>Rich tribal culture and traditions</li>
                    <li>Adventure activities and trekking</li>
                    <li>Local cuisine and hospitality</li>
                    <li>Photography and nature trails</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Detailed Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Guwahati to Shillong</h4>
                    <ul>
                      <li>Upon arrival at Guwahati Airport, our representative will meet & greet you all</li>
                      <li>Everyone will head to Shillong at 12 PM</li>
                      <li>Request everyone to book a flight arriving before 11 am at Guwahati Airport</li>
                      <li>Guwahati to Shillong is a 4 hours scenic drive</li>
                      <li>You will come across the Umiam Lake (Barapani) while travelling</li>
                      <li>Check-in at the hotel. Overnight at Shillong</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Shillong - Cherrapunji via Laitlum Canyon</h4>
                    <ul>
                      <li>Enjoy a lip-smacking breakfast in the morning</li>
                      <li>Get transferred to Cherrapunji</li>
                      <li>On the way, visit the breathtaking Laitlum Canyon, Mawkdok Dympep Viewpoint</li>
                      <li>Grasp a stunning view of Nohkalikai falls, the Garden of caves, Arwah Caves</li>
                      <li>Visit Seven Sisters' fall if time permits</li>
                      <li>Overnight stay at Cherrapunji</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Cherrapunji - Nongriat Trek - Cherrapunji</h4>
                    <ul>
                      <li>After breakfast at 7 am, proceed to Tyrna village</li>
                      <li>Start the trek to the Double Decker Root Bridge (Nongriat) with the local guide</li>
                      <li>Climb up the 3500 steps to end the hike at the same village by evening</li>
                      <li>Overnight at Cherrapunji</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Cherrapunji - Mawlynnong - Dawki</h4>
                    <ul>
                      <li>Enjoy a mouth-watering breakfast in the morning</li>
                      <li>Drive to Mawlynnong (110 km/ 02 1⁄2 hrs drive), which is the cleanest village in Asia</li>
                      <li>Later, trek to visit the Single Decker Living Root Bridge in Riwai village</li>
                      <li>Visit Balancing Rock post this to Dawki where you can do boating in crystal clear waters of Umngot River</li>
                      <li>Overnight at Dawki</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Shnongpdeng - Jowai - Shillong</h4>
                    <ul>
                      <li>Wake up to see the beauty of East Khasi Hills, & have Breakfast</li>
                      <li>You can do multiple adventure activities (on your own) like Kayaking, Boating, Zip lining, Cliff jumping, etc</li>
                      <li>Start for Jowai to visit India's most magical waterfall: Krang Suri Waterfalls</li>
                      <li>Get drenched and swim in the natural pools</li>
                      <li>Travel back to Shillong after witnessing this attractive place</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Shillong - Guwahati - Airport Drop</h4>
                    <ul>
                      <li>Morning after breakfast, we head towards Guwahati Airport around 9 am</li>
                      <li>It's a 4-5 hours drive to the airport; and will reach the Airport by 3 pm</li>
                      <li>Kindly book your flights post 5 pm</li>
                      <li>Bid farewell to this wonderful trip with cherished memories</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Pickup & Drop according to fixed timing</li>
                    <li><FaCheck className="check-icon" />Transportation for local sightseeing: In Tempo Traveler/SUV from Guwahati to Guwahati</li>
                    <li><FaCheck className="check-icon" />Stay for 5 Nights, specifically: 02 nights hotel stay in Shillong, 02 nights hotel stay in Cherrapunji, and 01 night stay at Dawki</li>
                    <li><FaCheck className="check-icon" />5 Breakfasts</li>
                    <li><FaCheck className="check-icon" />Complimentary bonfire at Dawki</li>
                    <li><FaCheck className="check-icon" />Nongriat Trek guide</li>
                    <li><FaCheck className="check-icon" />Tour captain for 06 days</li>
                    <li><FaCheck className="check-icon" />All toll, parking, fuel & driver allowance</li>
                    <li><FaCheck className="check-icon" />Operation executive on call for 24x7</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />5% GST</li>
                    <li><FaTimes className="times-icon" />Any Entry Fees of Temples, Museum, Park sets</li>
                    <li><FaTimes className="times-icon" />Personal expenses like tips, laundry, phone bills, any other meals apart from those mentioned in the inclusions</li>
                    <li><FaTimes className="times-icon" />Any additional sightseeing beyond the itinerary, pick-up/drop from any other points, or use of vehicle at night – these will incur extra costs</li>
                    <li><FaTimes className="times-icon" />Any additional Safari / Boating Guide charges</li>
                    <li><FaTimes className="times-icon" />Anything not explicitly mentioned in the Inclusions section</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 16,999</span>
                    <span className="duration">6 Days / 5 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Meghalaya, North East India</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: March to June</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 6-10 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Easy to Moderate</span>
                    </div>
                  </div>

                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Meghalaya Backpacking Tour"
                  tourPrice="₹ 16,999"
                  tourDuration="6 Days / 5 Nights"
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Meghalaya;
